/**
 * "Stuur ETA nu"-knop op order-detail — office-fallback voor de proactieve
 * Kleda dag-notificaties (belvolume-reductie). Toont zichzelf alleen voor
 * Kleda-orders in de relevante status; verstuurt idempotent via
 * `POST /api/notify/send`. Self-contained (eigen loading/feedback-state).
 */
import { useCallback, useState } from 'react';
import clsx from 'clsx';
import { sendOrderEta, type EtaKind } from '../../lib/send-eta';

/** Per moment: de WO-status waarin de knop zinvol is. */
const ELIGIBLE_STATUS: Record<EtaKind, string> = {
  aflever_eta: 'Terugbrengen ingepland',
  ophaal_eta: 'Ophalen ingepland',
  klaar: 'Terugbrengen plannen',
};

const LABEL: Record<EtaKind, string> = {
  aflever_eta: 'Stuur bezorg-ETA nu',
  ophaal_eta: 'Stuur ophaal-ETA nu',
  klaar: "Stuur 'kleed klaar'-SMS",
};

interface Props {
  // Kleda-scope via recordTypeDeveloperName='Wasserij' — `brand` is op vrijwel
  // alle WO's null en dus onbruikbaar als merkfilter (geverifieerd live).
  order: {
    id: string;
    recordTypeDeveloperName?: string;
    status: string;
    // `definitief` is alleen gevuld na route-lock; zonder venster geen ETA.
    pickupAppointment?: { definitief?: string | null } | null;
    deliveryAppointment?: { definitief?: string | null } | null;
  };
  kind: EtaKind;
}

export function SendEtaAction({ order, kind }: Props) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'info' | 'error'; text: string } | null>(null);

  const handleClick = useCallback(async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await sendOrderEta(order.id, kind);
      if (!r.ok) {
        const text =
          r.reason === 'Bevestigingen_sturen__c=false'
            ? 'Bevestigingen staan uit voor deze order.'
            : r.reason === 'geen-tijdvenster'
              ? 'Route nog niet gelockt — geen tijdvenster.'
              : r.reason || r.error || 'Versturen mislukt.';
        setMsg({ kind: 'error', text });
      } else if (r.sent > 0) {
        setMsg({ kind: 'ok', text: `Verstuurd via ${r.channels.join(' + ')}.` });
      } else if (r.suppressed > 0) {
        setMsg({ kind: 'info', text: 'Niet verstuurd — klant heeft SMS afgemeld.' });
      } else if (r.failed > 0) {
        setMsg({ kind: 'error', text: 'Versturen mislukt — probeer later opnieuw.' });
      } else {
        setMsg({ kind: 'info', text: 'Al verstuurd, of geen e-mail/mobiel bekend.' });
      }
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof Error ? err.message : 'Versturen mislukt.' });
    } finally {
      setBusy(false);
    }
  }, [order.id, kind]);

  // Alleen Kleda-wasserij-orders in de juiste status — anders niets tonen.
  if (order.recordTypeDeveloperName !== 'Wasserij' || order.status !== ELIGIBLE_STATUS[kind]) return null;

  // ETA's vereisen een definitief tijdvenster (route gelockt). Zonder venster zou
  // de mail "vandaag" zeggen zónder tijd — daarom blokkeren we de knop. De 'klaar'-
  // SMS heeft geen venster nodig. De backend weigert dit ook nog eens (autoritatief).
  const needsWindow = kind === 'ophaal_eta' || kind === 'aflever_eta';
  const hasWindow =
    kind === 'ophaal_eta'
      ? !!order.pickupAppointment?.definitief
      : kind === 'aflever_eta'
        ? !!order.deliveryAppointment?.definitief
        : true;
  const blockedNoWindow = needsWindow && !hasWindow;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={busy || blockedNoWindow}
        className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        title={
          blockedNoWindow
            ? 'Route nog niet gelockt — er is nog geen definitief tijdvenster om te communiceren.'
            : 'Stuur de klant nu proactief een bericht met het tijdvenster + self-service-link. Idempotent: dezelfde melding gaat niet dubbel.'
        }
      >
        {busy ? 'Versturen…' : LABEL[kind]}
      </button>
      {blockedNoWindow && (
        <p className="text-xs mt-1 text-slate-500">Route nog niet gelockt — geen tijdvenster.</p>
      )}
      {msg && (
        <p
          className={clsx(
            'text-xs mt-1',
            msg.kind === 'ok' && 'text-emerald-600',
            msg.kind === 'error' && 'text-red-600',
            msg.kind === 'info' && 'text-slate-500',
          )}
        >
          {msg.text}
        </p>
      )}
    </div>
  );
}
